import { useCallback, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ScreenRoot } from "../../../components/Shell";
import { useTopBar } from "../../../components/TopBar";
import { useT } from "../../../i18n";
import { ORG_REPORT_PATH } from "../../../constants/paths";
import { useOpsApi } from "../api/opsApi";
import { AssetsTab } from "./AssetsTab";
import { CatalogTab } from "./CatalogTab";
import { ConfigTab } from "./ConfigTab";
import { ModesTab } from "./ModesTab";
import { StageDecorTab } from "./StageDecorTab";
import { StageGoalsTab } from "./StageGoalsTab";
import { MonitorTab } from "./MonitorTab";
import "../../../styles/teacher.css";
import "../../../styles/ops.css";
import type { OpsTab } from "../types";

const TABS: readonly OpsTab[] = [
  "monitor",
  "catalog",
  "stages",
  "goals",
  "modes",
  "config",
  "assets",
];
const SCREEN: Record<OpsTab, string> = {
  monitor: "FS-15-01",
  catalog: "FS-15-02",
  modes: "FS-15-03",
  config: "FS-15-04",
  assets: "FS-15-05",
  stages: "FS-15-07",
  goals: "FS-15-08",
};

/**
 * FS-15 운영 콘솔(운영자 · SU/공통 · SCR v0.6 sfs-15 틀) — /ops/{monitor|catalog|modes|config|assets}.
 *  - 좌측 짙은 탭: 룸 모니터(FS-15-01 · P2-S4 — 첫 탭) · 카탈로그(02) · 모드 정의·추천(03) · 런타임 설정(04) · 자산·서비스 키(05)
 *    · 무대 꾸밈(FS-15-07 · 2026-10-08 — 기구 무대 장면을 인벤토리로 꾸미기 · 카탈로그 stage_scene)
 *    · 무대 목표(FS-15-08 · 2026-10-08 — 기구 무대 별 기준 · 문구 편집 · 정답으로 시험 · 새 버전 배포)
 *    · 기관 집계(FS-15-06 · P2-S6 — /org/report 로 잇는다)
 *  - 상단 바 '🛠 Field 운영 콘솔(· <탭>)' + 탭별 칩(활성 룸 n · 알림 n / 모드 n · 활성 n / vN · 적용됨 / brick-parts@버전)
 * 개인 식별 없음(가명·코스 ID) · 설정 변경은 신규 룸부터 · 모든 변경은 감사 로그(서버).
 */
export function OpsConsoleScreen({ tab }: { tab: OpsTab }) {
  const t = useT();
  const api = useOpsApi();
  const [modeSum, setModeSum] = useState<[number, number] | null>(null);
  const [cfgVer, setCfgVer] = useState<number | null>(null);
  const [latest, setLatest] = useState<string | null>(null);
  const [monSum, setMonSum] = useState<[number, number] | null>(null);
  const onSummary = useCallback((n: number, a: number) => setModeSum([n, a]), []);
  const onMonitor = useCallback((rooms: number, alerts: number) => setMonSum([rooms, alerts]), []);

  const monitorChips =
    tab === "monitor" && monSum
      ? [
          { label: t("ops.monitor.chipRooms", { n: monSum[0] }), testId: "ops-chip" },
          { label: t("ops.monitor.chipAlerts", { n: monSum[1] }), testId: "ops-alerts-chip" },
        ]
      : [];
  const chip =
    tab === "modes" && modeSum
      ? t("ops.modes.topChip", { n: modeSum[0], active: modeSum[1] })
      : tab === "config" && cfgVer != null
        ? t("ops.config.topChip", { v: cfgVer })
        : tab === "assets" && latest
          ? latest
          : null;
  useTopBar({
    icon: "🛠",
    // 와이어 sfs-15: 첫 탭(룸 모니터)은 '🛠 Field 운영 콘솔' 만
    title:
      tab === "monitor"
        ? t("ops.monitor.topTitle")
        : t("ops.topTitle", { tab: t(`ops.tab.${tab}`) }),
    chips: tab === "monitor" ? monitorChips : chip ? [{ label: chip, testId: "ops-chip" }] : [],
  });

  return (
    <ScreenRoot
      id={SCREEN[tab]}
      title={t(`ops.tab.${tab}`)}
      hideTitle
      className="pf-console"
      focusKey={tab}
    >
      <div className="pf-console__frame">
        <nav className="pf-console__side" aria-label={t("ops.title")}>
          <ul className="pf-console__tabs" data-testid="ops-tabs">
            {TABS.map((id) => (
              <li key={id}>
                <NavLink
                  to={`/ops/${id}`}
                  className={({ isActive }) =>
                    `pf-console__tab${isActive ? " pf-console__tab--on" : ""}`
                  }
                  data-tab={id}
                >
                  {t(`ops.tab.${id}`)}
                </NavLink>
              </li>
            ))}
            <li>
              {/* FS-15-06 기관 집계(P2-S6) — 기관 담당자와 같은 페이지(사이드 탭 없는 틀) */}
              <Link to={ORG_REPORT_PATH} className="pf-console__tab" data-tab="org">
                {t("ops.tab.org")}
              </Link>
            </li>
          </ul>
        </nav>
        <div className="pf-console__body">
          {!api ? (
            <p className="pf-muted">{t("common.loading")}</p>
          ) : tab === "monitor" ? (
            <MonitorTab api={api} onSummary={onMonitor} />
          ) : tab === "catalog" ? (
            <CatalogTab api={api} />
          ) : tab === "stages" ? (
            <StageDecorTab api={api} />
          ) : tab === "goals" ? (
            <StageGoalsTab api={api} />
          ) : tab === "modes" ? (
            <ModesTab api={api} onSummary={onSummary} />
          ) : tab === "config" ? (
            <ConfigTab api={api} onVersion={setCfgVer} />
          ) : (
            <AssetsTab api={api} onLatest={setLatest} />
          )}
          <p className="pf-note" data-testid="ops-footer">
            {t("ops.footer")}
          </p>
        </div>
      </div>
    </ScreenRoot>
  );
}
